import React from 'react';
import { Pencil, AlertTriangle, CreditCard } from 'lucide-react';
import classNames from 'classnames';

import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { FormattedMessage } from '../../../util/reactIntl';
import { generateLinkProps } from '../../../util/routes';
import {
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_CLOSED,
  LISTING_STATE_DRAFT,
  propTypes,
} from '../../../util/types';

import { NamedLink, ExternalLink } from '../../../components';
import { Badge } from '../../../components/ui/badge';
import EditIcon from '../EditIcon';

import css from '../ListingPage.module.css';

/**
 * CTAButtonMaybe: renders the approval CTA if enabled.
 */
const CTAButtonMaybe = props => {
  const { data, routeConfiguration, userId, userEmail, listingId, isPendingApproval } = props;

  const hasValidType = data?.type && data.type !== 'none';
  const isCTAEnabled = hasValidType && isPendingApproval;
  if (!isCTAEnabled) return null;

  const { type, text, href } = data;
  const ctaLink = generateLinkProps(type, href, routeConfiguration, userId, userEmail, listingId);
  const isInternalLink = type === 'internal' && ctaLink.route;

  return isInternalLink ? (
    <NamedLink name={ctaLink.route.name} to={ctaLink.route.to} params={ctaLink.route.params}>
      <Button size="sm" className="font-heading uppercase tracking-wide">{text}</Button>
    </NamedLink>
  ) : (
    <ExternalLink href={ctaLink.link}>
      <Button size="sm" className="font-heading uppercase tracking-wide">{text}</Button>
    </ExternalLink>
  );
};

const STATE_CONFIG = {
  [LISTING_STATE_DRAFT]: {
    label: 'Draft',
    badgeCls: 'border-zinc-400 text-zinc-700',
    editMsg: 'ListingPage.finishListing',
    statusMsg: 'ListingPage.ownListingDraft',
  },
  [LISTING_STATE_PENDING_APPROVAL]: {
    label: 'Pending Approval',
    badgeCls: 'border-blue-500/40 text-blue-700',
    editMsg: 'ListingPage.editListing',
    statusMsg: 'ListingPage.ownListingPendingApproval',
  },
  [LISTING_STATE_CLOSED]: {
    label: 'Closed',
    badgeCls: 'border-destructive/50 text-destructive',
    editMsg: 'ListingPage.editListing',
    statusMsg: 'ListingPage.ownClosedListing',
  },
};

const DEFAULT_STATE_CONFIG = {
  label: 'Live',
  badgeCls: 'border-green-500/40 text-green-700',
  editMsg: 'ListingPage.editListing',
  statusMsg: 'ListingPage.ownListing',
};

/**
 * The ActionBar component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {boolean} props.isOwnListing - Whether the listing is own
 * @param {propTypes.listing | propTypes.ownListing} props.listing - The listing
 * @param {propTypes.currentUser} props.currentUser - The current user
 * @param {Object} props.editParams - The path params for the named route to edit the listing
 * @param {string} props.editParams.id - The id
 * @param {string} props.editParams.slug - The slug
 * @param {'edit' | 'draft'} props.editParams.type - The type
 * @param {string} props.editParams.tab - The tab (e.g. 'details' or 'pricing')
 * @param {boolean} props.showNoPayoutDetailsSet - Show info about missing payout details
 * @returns {JSX.Element} action bar component
 */
export const ActionBar = props => {
  const {
    rootClassName,
    className,
    isOwnListing,
    listing,
    currentUser,
    editParams,
    showNoPayoutDetailsSet,
  } = props;
  const classes = classNames(rootClassName || css.actionBar, className);

  const state = listing.attributes.state;
  const isPendingApproval = state === LISTING_STATE_PENDING_APPROVAL;
  const isClosed = state === LISTING_STATE_CLOSED;

  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const approvalToPublishOptions =
    config?.accessControl?.listings?.requireApprovalToPublishOptions?.callToAction || {};

  // ── Missing payout details ───────────────────────────────────────────────
  if (isOwnListing && showNoPayoutDetailsSet) {
    return (
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 rounded-lg border border-border bg-muted/30 pl-0 pr-4 py-2.5 overflow-hidden">
        <div className="flex items-center gap-3">
          <div className="w-1 self-stretch bg-destructive rounded-l-lg shrink-0" />
          <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
          <span className="text-sm text-foreground">
            <FormattedMessage id="ListingPage.addPayoutDetailsMessage" />
          </span>
        </div>
        <NamedLink
          name="StripePayoutPage"
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-input bg-background text-sm font-heading uppercase tracking-wide hover:bg-accent hover:text-accent-foreground font-medium shrink-0"
        >
          <CreditCard className="h-3.5 w-3.5" />
          <FormattedMessage id="ListingPage.addPayoutDetails" />
        </NamedLink>
      </div>
    );
  }

  // ── Own listing bar ──────────────────────────────────────────────────────
  if (isOwnListing) {
    const stateConf = STATE_CONFIG[state] || DEFAULT_STATE_CONFIG;
    const hasValidType = approvalToPublishOptions?.type && approvalToPublishOptions.type !== 'none';
    const isCTAEnabled = hasValidType && isPendingApproval;

    return (
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 rounded-lg border border-border bg-muted/30 px-4 py-2.5">
        <div className="flex items-center gap-2.5">
          <Badge
            variant="outline"
            className={`font-heading text-[10px] uppercase tracking-widest font-bold ${stateConf.badgeCls}`}
          >
            {stateConf.label}
          </Badge>
          <span className="text-sm text-muted-foreground">
            <FormattedMessage id={stateConf.statusMsg} />
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <NamedLink
            name="EditListingPage"
            params={editParams}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-input bg-background text-sm font-heading uppercase tracking-wide hover:bg-accent hover:text-accent-foreground font-medium"
          >
            <Pencil className="h-3.5 w-3.5" />
            <FormattedMessage id={stateConf.editMsg} />
          </NamedLink>
          {isCTAEnabled && (
            <CTAButtonMaybe
              data={approvalToPublishOptions}
              routeConfiguration={routeConfiguration}
              listingId={listing?.id?.uuid}
              userId={currentUser?.id?.uuid}
              userEmail={currentUser?.attributes?.email}
              isPendingApproval={isPendingApproval}
            />
          )}
        </div>
      </div>
    );
  }

  // ── Closed listing (visitor view) ────────────────────────────────────────
  if (isClosed) {
    return (
      <div className="flex items-center gap-3 mb-6 rounded-lg border border-border bg-muted/30 pl-0 pr-4 py-2.5 overflow-hidden">
        <div className="w-1 self-stretch bg-destructive rounded-l-lg shrink-0" />
        <Badge variant="outline" className="border-destructive/50 text-destructive font-heading text-[10px] uppercase tracking-widest font-bold">
          Closed
        </Badge>
        <span className="text-sm text-muted-foreground">
          <FormattedMessage id="ListingPage.closedListing" />
        </span>
      </div>
    );
  }

  return null;
};

export default ActionBar;
